import { clamp } from "./plate";

export const KEYS = {
  HISTORY: "nrplate_history_v1",
  WELCOME: "vaya_taxi_welcome_seen_v1",
  LANG: "vaya_lang_v1",
  PROFILE: "vaya_profile_v1",
  STATS: "vaya_stats_v1",
  RATING_HISTORY: "vaya_rating_history_v1",
};

export function safeGet(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw === null ? fallback : raw;
  } catch {
    return fallback;
  }
}

export function safeSet(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch {
    // ignore
  }
}

export function safeLoadHistory() {
  try {
    const raw = localStorage.getItem(KEYS.HISTORY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];

    return parsed
      .filter((x) => x && typeof x.plate === "string")
      .map((x) => ({
        plate: x.plate,
        score: typeof x.score === "number" ? clamp(x.score, 0, 5) : null,
        stars: typeof x.stars === "number" ? clamp(Math.round(x.stars), 0, 5) : 0,
        ts: typeof x.ts === "number" ? x.ts : Date.now(),
      }))
      .sort((a, b) => (b.ts || 0) - (a.ts || 0))
      .slice(0, 12);
  } catch {
    return [];
  }
}

export function safeSaveHistory(history) {
  try {
    localStorage.setItem(KEYS.HISTORY, JSON.stringify(history));
  } catch {
    // ignore
  }
}

export function localDateKey(date = new Date()) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function safeLoadRatingHistory() {
  try {
    const parsed = JSON.parse(localStorage.getItem(KEYS.RATING_HISTORY) || "{}");
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};

    return Object.fromEntries(
      Object.entries(parsed)
        .filter(([plate, entries]) => typeof plate === "string" && Array.isArray(entries))
        .map(([plate, entries]) => [
          plate,
          entries
            .filter((entry) =>
              entry &&
              /^\d{4}-\d{2}-\d{2}$/.test(entry.date) &&
              typeof entry.score === "number"
            )
            .map((entry) => ({ date: entry.date, score: clamp(entry.score, 0, 5) }))
            .sort((a, b) => a.date.localeCompare(b.date))
            .slice(-365),
        ])
        .filter(([, entries]) => entries.length)
    );
  } catch {
    return {};
  }
}

export function recordDailyRatingSnapshots(taxis, date = new Date()) {
  const saved = safeLoadRatingHistory();
  const day = localDateKey(date);
  let changed = false;

  taxis.forEach(({ plate, score }) => {
    if (typeof plate !== "string" || typeof score !== "number") return;
    const entries = saved[plate] ? [...saved[plate]] : [];
    const existingIndex = entries.findIndex((entry) => entry.date === day);
    const snapshot = { date: day, score: clamp(score, 0, 5) };

    if (existingIndex === -1) {
      entries.push(snapshot);
      changed = true;
    } else if (entries[existingIndex].score !== snapshot.score) {
      entries[existingIndex] = snapshot;
      changed = true;
    }
    saved[plate] = entries.sort((a, b) => a.date.localeCompare(b.date)).slice(-365);
  });

  if (changed) {
    try {
      localStorage.setItem(KEYS.RATING_HISTORY, JSON.stringify(saved));
    } catch {
      // ignore
    }
  }
  return saved;
}

export function safeLoadJson(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? { ...fallback, ...parsed } : fallback;
  } catch {
    return fallback;
  }
}

export function safeSaveJson(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // ignore
  }
}
